import React, { useState } from 'react'
import PropTypes from 'prop-types'
import mime from 'mime-types'
import { cx } from '@emotion/css'

import ClipIcon from '@offerzen/zenicon/ClipIcon'
import Attachment from '../../../elements/Attachment'
import { buttonSize, buttonVariant } from '../../../elements/Button'
import AccessibleIcon from '../../../elements/AccessibleIcon'
import FormHelpText from '../../../elements/FormHelpText'
import FormErrorMessage from '../../../elements/FormErrorMessage'

import styles from './styles'

const DEFAULT_MEGABYTE_FILE_SIZE_LIMIT = 10

const FileUpload = ({
  id,
  label = 'Attach file',
  meta = {},
  input = {},
  helpText,
  sizeLimit = DEFAULT_MEGABYTE_FILE_SIZE_LIMIT,
  theme: themeProp = 'primary',
  size = 'default',
  variant = 'default',
  className,
}) => {
  const [error, setError] = useState()

  const handleUpload = ev => {
    const file = ev.target.files[0]

    if (!file) {
      setError('There is no file attached')
      return
    }

    const sizeLimitInBytes = sizeLimit * 1000000
    if (file.size >= sizeLimitInBytes) {
      setError(`The file exceeds the ${sizeLimit}MB limit`)
      return
    }

    const mimeType = mime.lookup(file.name)
    if (!mimeType) {
      setError('The file format is invalid')
      return
    }

    setError(null)
    const handler = input.onChange
    if (handler) handler(ev)
  }

  const onBlur = ev => {
    const handler = input.onBlur
    if (!handler) return

    if (error || !ev.target.files[0]) {
      handler()
      return
    }

    handler(ev)
  }

  const clearUpload = () => {
    setError(null)
    const handler = input.onChange
    if (handler) handler(null)
  }

  const errored = (meta.touched && meta.error) || error
  const theme = errored ? 'error' : themeProp

  // removes value because the file input is always uncontrolled – https://reactjs.org/docs/uncontrolled-components.html#the-file-input-tag
  const { value, ...fileInput } = input
  const file = value && value[0]

  return (
    <div className={cx(styles.wrapper, className)}>
      {file ? (
        <Attachment name={file.name} theme={theme} size={size} onClear={clearUpload} />
      ) : (
        <label htmlFor={id} className={cx(styles.label(theme), buttonSize(size), buttonVariant(variant, theme))}>
          <AccessibleIcon icon={ClipIcon} size={14} color={styles.icon(theme, variant).color} />
          <span className={styles.labelText}>{label}</span>
          <input
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...fileInput}
            id={id}
            type="file"
            onChange={handleUpload}
            onBlur={onBlur}
            className={styles.input}
          />
        </label>
      )}
      {Boolean(helpText && !errored && !file) && <FormHelpText text={helpText} />}
      {Boolean(errored) && <FormErrorMessage text={errored} />}
    </div>
  )
}

FileUpload.propTypes = {
  id: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  input: PropTypes.shape({
    name: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.shape({
        0: PropTypes.shape({
          name: PropTypes.string,
          size: PropTypes.number,
          type: PropTypes.string,
        }),
      }),
      PropTypes.string,
    ]),
  }).isRequired,
  label: PropTypes.string,
  helpText: PropTypes.string,
  sizeLimit: PropTypes.number,
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'light', 'dark', 'tertiary', 'info']),
  className: PropTypes.string,
  size: PropTypes.oneOf(['', 'default', 'small']),
  variant: PropTypes.oneOf(['', 'default', 'outlined', 'dashed']),
}

export default FileUpload
